import React, { useState, useEffect, useCallback } from "react"
import { Form, Row, Col, Button, Alert } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom"
import Layout from "../components/layout/Layout"
import useAuthAxios from "../hooks/useAuthAxios"
import { useAuth } from "../context/AuthContext"
import { filterEmptyFields } from "../utils/searchUtils"
import { usePersistedState } from "../hooks/usePersistedState"
import { formatDateForDisplay, formatDateForApi } from "../utils/dateUtils"
import { toast } from "react-toastify"
import DataTable from "../components/common/DataTable"
import DateTimePicker from "../components/common/DateTimePicker"

const DEFAULT_SEARCH_CRITERIA = {
  InspectionID: null,
  Status: [],
  IssueDateTimeFrom: null,
  IssueDateTimeTo: null,
  ExpertAssignmentDateFrom: null,
  ExpertAssignmentDateTimeTo: null,
  CompletionDateTimeFrom: null,
  CompletionDateTimeTo: null,
  Surname: "",
  ExpertID: null,
  VehiclePlateNumber: "",
  Top: 100
}

const InspectionsList = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { get, loading, error } = useAuthAxios()
  const { getAppLookupValue, isAdmin, appLookups } = useAuth()
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  // Use persisted state for search results and criteria
  const [inspections, setInspections] = usePersistedState("inspections", [])
  const [hasSearched, setHasSearched] = useState(false)
  const [searchCriteria, setSearchCriteria] = usePersistedState("searchCriteria", DEFAULT_SEARCH_CRITERIA)

  // Clear persisted state when navigating away from inspections routes
  useEffect(() => {
    return () => {
      // Get the next route from the browser's history state
      const nextPath = window.location.pathname

      // Only clear if navigating away from inspections routes
      if (!nextPath.startsWith("/inspections")) {
        // Clear React state
        setInspections([])
        setSearchCriteria(DEFAULT_SEARCH_CRITERIA)

        // Clear localStorage
        localStorage.removeItem("inspections")
        localStorage.removeItem("searchCriteria")
      }
    }
  }, [setInspections, setSearchCriteria])

  // Get status options from appLookups
  const statusOptions = appLookups
    ? appLookups.filter(lookup => lookup.Table === "Status").sort((a, b) => a.Code - b.Code)
    : []

  // Handle search form submission
  const handleSearch = useCallback(
    async e => {
      if (e) {
        e.preventDefault()
      }
      try {
        setHasSearched(true)
        const filteredCriteria = filterEmptyFields(searchCriteria)
        filteredCriteria.CompletionDateTimeFrom = formatDateForApi(filteredCriteria.CompletionDateTimeFrom)
        filteredCriteria.CompletionDateTimeTo = formatDateForApi(filteredCriteria.CompletionDateTimeTo)
        filteredCriteria.ExpertAssignmentDateFrom = formatDateForApi(
          filteredCriteria.ExpertAssignmentDateFrom
        )
        filteredCriteria.ExpertAssignmentDateTimeTo = formatDateForApi(
          filteredCriteria.ExpertAssignmentDateTimeTo
        )
        filteredCriteria.IssueDateTimeFrom = formatDateForApi(filteredCriteria.IssueDateTimeFrom)
        filteredCriteria.IssueDateTimeTo = formatDateForApi(filteredCriteria.IssueDateTimeTo)
        const response = await get("/inspections", { params: filteredCriteria })
        const items = response.data || []
        setInspections(items)
      } catch (err) {
        console.error("Failed to fetch inspections:", err)
        if (err.response?.status !== 404) {
          // Don't show toast for no results
          toast.error("Αποτυχία φόρτωσης ελέγχων. Παρακαλώ, προσπαθήστε ξανά.")
        }
        setInspections([])
      }
    },
    [get, searchCriteria, setInspections]
  )

  // Handle status from navigation state
  useEffect(() => {
    const handleInitialLoad = async () => {
      if (!initialLoadDone && location.state?.status !== undefined) {
        try {
          const status = location.state.status
          // Only set status if it's different from current
          if (!searchCriteria.Status.length || searchCriteria.Status[0].short !== status) {
            await setSearchCriteria(prev => ({
              ...prev,
              Status: [{ short: status }]
            }))
          }
        } catch (error) {
          console.error("Failed to set initial status:", error)
          toast.error("Αποτυχία αρχικοποίησης αναζήτησης.")
        } finally {
          // Clear the state after using it
          navigate(location.pathname, { replace: true, state: {} })
          setInitialLoadDone(true)
        }
      } else {
        setInitialLoadDone(true)
      }
    }

    handleInitialLoad()
  }, [location.state, location.pathname, initialLoadDone, navigate, setSearchCriteria, searchCriteria.Status])

  // Handle search when status changes
  useEffect(() => {
    if (initialLoadDone && searchCriteria.Status.length > 0 && undefined !== location.state?.status) {
      handleSearch()
    }
  }, [searchCriteria.Status, initialLoadDone, handleSearch, location.state?.status])

  // Handle reset
  const handleReset = useCallback(() => {
    setSearchCriteria(DEFAULT_SEARCH_CRITERIA)
    setInspections([])
    setHasSearched(false)
  }, [setSearchCriteria, setInspections])

  // Handle search criteria changes
  const handleCriteriaChange = (field, value) => {
    // For "from" date fields, set time to 00:00:00
    if (field.endsWith("From") && value) {
      const date = new Date(value)
      date.setHours(0, 0, 0, 0)
      value = date.toISOString()
    }
    // For "to" date fields, set time to 23:59:59
    else if (field.endsWith("To") && value) {
      const date = new Date(value)
      date.setHours(23, 59, 59, 999)
      value = date.toISOString()
    }

    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Helper functions for status handling
  const getStatusBadgeColor = status => {
    // Using the exact same colors as in the dashboard
    switch (status) {
      case 0:
        return "custom-secondary" // #6c757d - Unassigned
      case 1:
        return "custom-warning" // #ffb81e - Pending
      case 2:
        return "custom-primary" // #0d6efd - E-Expert
      case 10:
        return "custom-success" // #198754 - Completed
      case 99:
        return "custom-danger" // #dc3545 - Rejected
      default:
        return "custom-secondary"
    }
  }

  const getStatusLabel = status => {
    const statusName = getAppLookupValue("Status", status)
    return statusName ?? "Unknown"
  }

  // Add custom styles to match dashboard colors exactly
  useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
            .badge.bg-custom-secondary { background-color: #6c757d !important; }
            .badge.bg-custom-warning { background-color: #ffb81e !important; }
            .badge.bg-custom-primary { background-color: #0d6efd !important; }
            .badge.bg-custom-success { background-color: #198754 !important; }
            .badge.bg-custom-danger { background-color: #dc3545 !important; }
        `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  const handleViewInspection = id => {
    navigate(`/inspections/${id}`)
  }

  return (
    <Layout>
      <Form onSubmit={handleSearch}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Έλεγχοι</h5>
        </div>
        <Row className="g-3">
          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">ID Ελέγχου</Form.Label>
              <Form.Control
                type="number"
                value={searchCriteria.InspectionID || ""}
                onChange={e =>
                  handleCriteriaChange("InspectionID", e.target.value ? parseInt(e.target.value, 10) : null)
                }
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Κατάσταση</Form.Label>
              <Form.Select
                value={searchCriteria.Status.length > 0 ? searchCriteria.Status[0].short : ""}
                onChange={e =>
                  handleCriteriaChange(
                    "Status",
                    e.target.value ? [{ short: parseInt(e.target.value, 10) }] : []
                  )
                }
                disabled={loading}>
                <option value="">Όλες</option>
                {statusOptions.map(status => (
                  <option key={status.Code} value={status.Code}>
                    {status.Name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Καταχώρησης Από</Form.Label>
              <DateTimePicker
                value={searchCriteria.IssueDateTimeFrom}
                onChange={value => handleCriteriaChange("IssueDateTimeFrom", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Καταχώρησης Έως</Form.Label>
              <DateTimePicker
                value={searchCriteria.IssueDateTimeTo}
                onChange={value => handleCriteriaChange("IssueDateTimeTo", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. ExpertCall Από</Form.Label>
              <DateTimePicker
                value={searchCriteria.ExpertAssignmentDateFrom}
                onChange={value => handleCriteriaChange("ExpertAssignmentDateFrom", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. ExpertCall Έως</Form.Label>
              <DateTimePicker
                value={searchCriteria.ExpertAssignmentDateTimeTo}
                onChange={value => handleCriteriaChange("ExpertAssignmentDateTimeTo", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Ολοκλήρωσης Από</Form.Label>
              <DateTimePicker
                value={searchCriteria.CompletionDateTimeFrom}
                onChange={value => handleCriteriaChange("CompletionDateTimeFrom", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Ολοκλήρωσης Έως</Form.Label>
              <DateTimePicker
                value={searchCriteria.CompletionDateTimeTo}
                onChange={value => handleCriteriaChange("CompletionDateTimeTo", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Επώνυμο Ιδιοκτήτη</Form.Label>
              <Form.Control
                type="text"
                value={searchCriteria.Surname}
                onChange={e => handleCriteriaChange("Surname", e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          {isAdmin() && (
            <Col lg={3} md={6} xs={12}>
              <Form.Group className="h-100">
                <Form.Label className="d-block text-nowrap mb-1">ID Πραγματογνώμονα</Form.Label>
                <Form.Control
                  type="number"
                  value={searchCriteria.ExpertID || ""}
                  onChange={e =>
                    handleCriteriaChange("ExpertID", e.target.value ? parseInt(e.target.value, 10) : null)
                  }
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          )}

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Αρ. Κυκλοφορίας</Form.Label>
              <Form.Control
                type="text"
                value={searchCriteria.VehiclePlateNumber}
                onChange={e => handleCriteriaChange("VehiclePlateNumber", e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col xs={12} className="mt-3">
              <Alert variant="info" className="mb-0 d-flex align-items-center gap-5">
                  {searchCriteria.Top !== "" && (
                    <small>
                      <i className="fas fa-info-circle me-1" />
                      Η αναζήτηση επιστρέφει τα πρώτα 100 αποτελέσματα.
                    </small>
                  )}
                  <Form.Check
                  type="checkbox"
                  id="returnAllResults"
                  label="Επιστροφή όλων των αποτελεσμάτων"
                  checked={searchCriteria.Top === ""}
                  onChange={e => {
                    handleCriteriaChange("Top", e.target.checked ? "" : 100)
                  }}
                  disabled={loading}
                />
              </Alert>
          </Col>

          <Col xs={12} className="mt-4">
            <div className="d-flex justify-content-between">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Αναζήτηση...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Αναζήτηση
                  </>
                )}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={loading}>
                <i className="fas fa-undo me-2" />
                Επαναφορά
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <hr className="my-4" />

      {/* Results Section */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Φόρτωση...</span>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-5 text-danger">
          <i className="fas fa-exclamation-circle fa-2x mb-3" />
          <p className="mb-0">Παρουσιάστηκε σφάλμα κατά την αναζήτηση.</p>
        </div>
      ) : inspections.length > 0 ? (
        <DataTable
          data={inspections}
          loading={loading}
          error={error}
          onRowClick={item => handleViewInspection(item.InspectionID)}
          columns={[
            {
              header: "Αίτηση",
              field: "InspectionID",
              type: "number"
            },
            {
              header: "Ημ.Καταχώρησης",
              field: "IssueDateTime",
              type: "date",
              render: item => formatDateForDisplay(item.IssueDateTime)
            },
            {
              header: "Status",
              field: "Status",
              type: "number",
              render: item => (
                <span className={`badge bg-${getStatusBadgeColor(item.Status)}`}>
                  {getStatusLabel(item.Status)}
                </span>
              ),
              sortValue: item => item.Status
            },
            {
              header: "Πραγματογνώμονας",
              field: "ExpertName",
              type: "string"
            },
            {
              header: "Ημ. ExpertCall",
              field: "ExpertAssignmentDate",
              type: "date",
              render: item => formatDateForDisplay(item.ExpertAssignmentDate)
            },
            {
              header: "Ημ. Ολοκλήρωσης",
              field: "CompletionDateTime",
              type: "date",
              render: item => formatDateForDisplay(item.CompletionDateTime)
            },
            {
              header: "Ιδιοκτήτης",
              field: "Surname",
              type: "string"
            },
            {
              header: "Αρ. Κυκλοφορίας",
              field: "PlateNumber",
              type: "string"
            },
            {
              header: "Αρ. Φωτο.",
              field: "DocNo",
              type: "number"
            }
          ]}
        />
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-search fa-2x mb-3" />
          <p className="mb-0">Δεν βρέθηκαν αποτελέσματα που να αντιστοιχούν στα κριτήρια αναζήτησης.</p>
        </div>
      ) : null}
    </Layout>
  )
}

export default InspectionsList
